import { Box, ButtonBase, Stack, Typography } from "@mui/material";
import LogisticsInsightsTypeGroupCard from "../common/LogisticsInsightsTypeGroupCard";

// Matches the teal used for section labels ("FILE INFO", "RELATED ASSETS", etc.)
// elsewhere in the app. Swap for a theme token (e.g. theme.palette.secondary.main)
// if one already maps to this color.
const SECTION_TEAL = "#0F8B8D";

// Section heading style, matching "FILE INFO" / "RELATED ASSETS" / "TIMELINE".
const SectionHeading = ({ children, sx }) => (
  <Typography
    sx={{
      fontSize: 12.5,
      fontWeight: 800,
      letterSpacing: 0.6,
      textTransform: "uppercase",
      color: SECTION_TEAL,
      ...sx,
    }}
  >
    {children}
  </Typography>
);

// Wraps the "Insights" title in a clickable ButtonBase when the dashboard
// can be opened; otherwise renders the same title as plain, non-interactive
// text (unchanged from before the dashboard existed).
const InsightsTitle = ({ isClickable, onOpenInsightsDashboard, sx, component }) => {
  const title = (
    <Typography component={component} data-testid="logistics-insights-title" sx={sx}>
      Insights
    </Typography>
  );

  if (!isClickable) return title;

  return (
    <ButtonBase
      onClick={onOpenInsightsDashboard}
      aria-haspopup="dialog"
      data-testid="logistics-insights-title-button"
      sx={{
        borderRadius: 1,
        px: 0.5,
        mx: -0.5,
        "&:hover": { backgroundColor: "action.hover" },
        "&:focus-visible": {
          outline: "2px solid",
          outlineColor: "primary.main",
          outlineOffset: 2,
        },
      }}
    >
      {title}
    </ButtonBase>
  );
};

const LogisticsRiskSidePanel = ({
  statistics,
  asset,
  selectedAssets = [],
  variant = "sidePanel",
  emptyInsightsMessage = "No insights available for this level.",
  onOpenInsightsDashboard,
}) => {
  const insightGroups = Array.isArray(statistics?.byType)
    ? statistics.byType
    : [];
  const hasInsights = insightGroups.length > 0;
  const hasSelectionContext = Boolean(asset) || selectedAssets.length > 0;
  const isEmbedded = variant === "embedded";
  const isInsightsClickable =
    hasInsights && typeof onOpenInsightsDashboard === "function";

  // Panel title bar — grey bar with a bottom divider, like "FILE DETAILS".
  const header = !isEmbedded && (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        px: { xs: 2, lg: 2.5 },
        py: 1.5,
        borderBottomColor: "divider",
      }}
    >
      <InsightsTitle
        isClickable={isInsightsClickable}
        onOpenInsightsDashboard={onOpenInsightsDashboard}
        sx={{
          fontSize: 13,
          fontWeight: 800,
          letterSpacing: 0.7,
          textTransform: "uppercase",
          color: "primary.main",
        }}
      />
    </Box>
  );

  const body = (
    <Box
      data-has-selection-context={hasSelectionContext ? "true" : undefined}
      sx={{
        py: isEmbedded ? 2 : { xs: 2, lg: 2.5 },
        px: isEmbedded ? 0 : { xs: 2, lg: 2.5 },
      }}
    >
      {isEmbedded && (
        <Box sx={{ mb: 2 }}>
          <InsightsTitle
            isClickable={isInsightsClickable}
            onOpenInsightsDashboard={onOpenInsightsDashboard}
            component="h6"
            sx={{ fontSize: "1.25rem", fontWeight: 800, color: "primary.main" }}
          />
        </Box>
      )}

      <Typography
        variant="title2"
        data-testid="asset-type-title"
        sx={{ fontWeight: 700, mb: 2, color: "primary.main" }}
      >
        By Asset Type
      </Typography>

      {hasInsights ? (
        <Stack spacing={2}>
          {insightGroups.map((group) => {
            const groupKey = group.type?.id || group.type?.name || "unknown";
            const typeName = group.type?.name || "Unclassified";
            const total = Number(group.total || 0);
            const stages = Array.isArray(group.stages) ? group.stages : [];

            return (
              <LogisticsInsightsTypeGroupCard
                key={groupKey}
                typeName={typeName}
                total={total}
                stages={stages}
              />
            );
          })}
        </Stack>
      ) : (
        <Box
          sx={{
            py: 4,
            px: 2,
            border: "1px dashed",
            borderColor: "divider",
            borderRadius: 2,
            textAlign: "center",
            backgroundColor: "grey.50",
          }}
        >
          <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
            {emptyInsightsMessage}
          </Typography>
        </Box>
      )}
    </Box>
  );

  if (isEmbedded) {
    return (
      <Box
        component="section"
        data-testid="logistics-risk-panel-embedded"
        sx={{
          mt: 2,
          borderTop: "1px solid",
          borderTopColor: "grey.200",
        }}
      >
        {body}
      </Box>
    );
  }

  return (
    <Box
      component="aside"
      data-testid="logistics-risk-panel"
      sx={{
        width: { xs: "100%", sm: 220, md: 280, lg: 360, xl: 400 },
        flexShrink: 0,
        borderLeft: { xs: 0, sm: "1px solid" },
        borderLeftColor: { sm: "grey.200" },
        bgcolor: "background.paper",
        minHeight: 0,
        overflowY: { xs: "visible", sm: "auto" },
      }}
    >
      {header}
      {body}
    </Box>
  );
};

export default LogisticsRiskSidePanel;
